'use client';

import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { useComplaints } from '@/lib/service-api';
import { formatOrderSubNr } from '@/lib/utils/format-order';

// Interface for table row display
export interface ComplaintTableRow {
  id: string;
  orderNumber: string;
  deliveryNote: string;
  date: string;
  shippingName: string;
  shippingStreet: string;
  shippingPostCode: string;
  shippingCity: string;
  commission: string;
  supportPerson: string;
  status: string;
}

interface ComplaintOverviewProps {
  customerNr: string;
  itemsPerPage?: number;
}

export function ComplaintOverview({
  customerNr,
  itemsPerPage = 10,
}: ComplaintOverviewProps) {
  const { t } = useTranslation('common');
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);

  // Fetch complaints from API
  const { data, loading, error } = useComplaints(customerNr, 'All');

  // Transform API data to table format
  const items: ComplaintTableRow[] =
    data?.map((complaint) => ({
      id: complaint.header.complaintId?.toString() || '',
      orderNumber: complaint.header.orderNr.toString(),
      deliveryNote: formatOrderSubNr(complaint.header.orderSubNr),
      date: '', // Date field left empty as per requirements
      shippingName: complaint.header.shippingAddress.name,
      shippingStreet: complaint.header.shippingAddress.street,
      shippingPostCode: complaint.header.shippingAddress.postCode,
      shippingCity: complaint.header.shippingAddress.city,
      commission: complaint.header.commission,
      supportPerson: complaint.header.status?.supportPerson || '',
      status: complaint.header.status?.textStatus || '',
    })) || [];

  // Sort completed complaints to the end
  items.sort((a, b) => {
    const aCompleted = a.status === 'Abgeschlossen' ? 1 : 0;
    const bCompleted = b.status === 'Abgeschlossen' ? 1 : 0;
    return aCompleted - bCompleted;
  });

  // Calculate pagination
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = items.slice(startIndex, endIndex);

  // Handle PDF download
  const handleDownloadPdf = async (complaintId: string) => {
    try {
      setDownloadingPdfId(complaintId);

      const response = await fetch(`/api/service/complaint/${complaintId}/overview`);

      if (!response.ok) {
        throw new Error(t('complaint.overview.pdf.error_load'));
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reklamation-${complaintId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Fehler beim Download:', error);
      alert(
        error instanceof Error
          ? error.message
          : t('complaint.overview.pdf.error_generic')
      );
    } finally {
      setDownloadingPdfId(null);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > maxPagesToShow) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="mx-auto w-full max-w-screen-desktop">
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="flex flex-col items-center gap-4">
            <div className="size-12 animate-spin rounded-full border-4 border-gray-300 border-t-black"></div>
            <p className="text-p1CopyMobile text-gray-600 lg:text-p1CopyDesktop">
              {t('complaint.overview.loading')}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="mx-10 mb-8 rounded-lg bg-red-50 p-6">
          <p className="text-p1CopyMobile font-semibold text-red-800 lg:text-p1CopyDesktop">
            {t('complaint.overview.error_title')}
          </p>
          <p className="mt-2 text-p2CopyMobile text-red-600 lg:text-p2CopyDesktop">
            {error}
          </p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && items.length === 0 && (
        <div className="mx-10 mb-8 rounded-lg bg-gray-100 p-6">
          <p className="text-p1CopyMobile text-gray-600 lg:text-p1CopyDesktop">
            {t('complaint.overview.empty')}
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && items.length > 0 && (
        <>
          {/* Mobile Card View */}
          <div className="space-y-4 tablet:hidden">
            {currentItems.map((item) => (
              <div key={item.id} className="border-b border-black p-4">
                <div className="mb-4 border-black pb-4">
                  <p className="text-p1CopySemiboldMobile">
                    {item.orderNumber}
                    {item.deliveryNote && (
                      <span className="ml-2 font-normal">{item.deliveryNote}</span>
                    )}
                  </p>
                  {item.date && (
                    <p className="mt-1 text-p2CopyMobile text-gray-600">
                      {item.date}
                    </p>
                  )}
                </div>
                <div className="space-y-2 text-p2CopyMobile">
                  {item.shippingName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('complaint.overview.labels.completion_at')}
                      </span>
                      <span className="text-right">
                        {item.shippingName}
                        {item.shippingStreet && <br />}
                        {item.shippingStreet}
                        {(item.shippingPostCode || item.shippingCity) && <br />}
                        {item.shippingPostCode} {item.shippingCity}
                      </span>
                    </div>
                  )}
                  {item.commission && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('complaint.overview.labels.commission')}
                      </span>
                      <span>{item.commission}</span>
                    </div>
                  )}
                  {item.supportPerson && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('complaint.overview.labels.support_person')}
                      </span>
                      <span>{item.supportPerson}</span>
                    </div>
                  )}
                  {item.status && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {t('complaint.overview.labels.status')}
                      </span>
                      <span>
                        {t(`complaint.overview.status.${item.status}`, {
                          defaultValue: item.status,
                        })}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex justify-end pt-4">
                  <button
                    onClick={() => handleDownloadPdf(item.id)}
                    disabled={downloadingPdfId === item.id}
                    className="text-p2CopyMobile underline hover:no-underline disabled:cursor-wait disabled:opacity-50"
                  >
                    {downloadingPdfId === item.id
                      ? t('complaint.overview.pdf.loading')
                      : t('complaint.overview.pdf.download')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden overflow-x-auto px-10 tablet:block">
            <table className="w-full">
              <thead>
                <tr className="border-y border-black">
                  <th className="pb-4 pt-6 text-left text-p2CopyMobile uppercase tracking-wider lg:text-p2CopyDesktop">
                    {t('complaint.overview.headers.order')}
                  </th>
                  <th className="pb-4 pt-6 text-left text-p2CopyMobile uppercase tracking-wider lg:text-p2CopyDesktop">
                    {t('complaint.overview.headers.completion_at')}
                  </th>
                  <th className="pb-4 pt-6 text-left text-p2CopyMobile uppercase tracking-wider lg:text-p2CopyDesktop">
                    {t('complaint.overview.headers.commission')}
                  </th>
                  <th className="pb-4 pt-6 text-left text-p2CopyMobile uppercase tracking-wider lg:text-p2CopyDesktop">
                    {t('complaint.overview.headers.support_person')}
                  </th>
                  <th className="pb-4 pt-6 text-left text-p2CopyMobile uppercase tracking-wider lg:text-p2CopyDesktop">
                    {t('complaint.overview.headers.status')}
                  </th>
                  <th className="pb-4 pt-6 text-p2CopyMobile uppercase tracking-wider lg:text-p2CopyDesktop" />
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id} className="border-b border-black">
                    <td className="py-6">
                      <div className="space-y-1">
                        <p className="text-p1CopySemiboldMobile lg:text-p1CopySemiboldDesktop">
                          {item.orderNumber}
                          {item.deliveryNote && (
                            <span className="ml-8 text-p1CopyMobile lg:text-p1CopyDesktop">
                              {item.deliveryNote}
                            </span>
                          )}
                        </p>
                        <p className="text-p1CopyMobile lg:text-p1CopyDesktop">
                          {item.date}
                        </p>
                      </div>
                    </td>
                    <td className="py-6">
                      <div className="space-y-1">
                        <p className="text-p1CopyMobile lg:text-p1CopyDesktop">
                          {item.shippingName}
                        </p>
                        <p className="text-p1CopyMobile lg:text-p1CopyDesktop">
                          {item.shippingStreet}
                        </p>
                        <p className="text-p1CopyMobile lg:text-p1CopyDesktop">
                          {item.shippingPostCode} {item.shippingCity}
                        </p>
                      </div>
                    </td>
                    <td className="py-6">
                      <p className="text-p1CopyMobile lg:text-p1CopyDesktop">
                        {item.commission}
                      </p>
                    </td>
                    <td className="py-6">
                      <p className="text-p1CopyMobile lg:text-p1CopyDesktop">
                        {item.supportPerson}
                      </p>
                    </td>
                    <td className="py-6">
                      <p className="text-p1CopyMobile lg:text-p1CopyDesktop">
                        {t(`complaint.overview.status.${item.status}`, {
                          defaultValue: item.status,
                        })}
                      </p>
                    </td>
                    <td className="py-6 text-right">
                      <button
                        onClick={() => handleDownloadPdf(item.id)}
                        disabled={downloadingPdfId === item.id}
                        className="text-p1CopyMobile underline hover:no-underline disabled:cursor-wait disabled:opacity-50 lg:text-p1CopyDesktop"
                      >
                        {downloadingPdfId === item.id
                          ? t('complaint.overview.pdf.loading')
                          : t('complaint.overview.pdf.short')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="flex size-[45px] items-center justify-center border border-black disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t('complaint.overview.pagination.previous')}
              >
                <svg
                  width="10"
                  height="17"
                  viewBox="0 0 10 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="rotate-180"
                >
                  <path
                    d="M1 1.5L8.5 8.5L1 15.5"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>

              {/* Page Numbers */}
              {getPageNumbers().map((page, index) => {
                if (page === '...') {
                  return (
                    <div
                      key={`ellipsis-${index}`}
                      className="flex size-[45px] items-center justify-center border border-black"
                    >
                      <span className="text-h5Mobile lg:text-h5Desktop">...</span>
                    </div>
                  );
                }

                const pageNumber = page as number;
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`flex size-[43px] items-center justify-center border border-black text-h5Mobile lg:text-h5Desktop ${
                      currentPage === pageNumber
                        ? 'bg-black text-white'
                        : 'bg-white text-black hover:bg-gray-100'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              {/* Next Button */}
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className="flex size-[45px] items-center justify-center border border-black disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t('complaint.overview.pagination.next')}
              >
                <svg
                  width="10"
                  height="17"
                  viewBox="0 0 10 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1.5L8.5 8.5L1 15.5"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
